import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Search } from 'lucide-react';
import SupplementCard from '../components/SupplementCard';
import { supplements, supplementCategories } from '../data/supplements';

const SupplementsEducationalPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  // Filter supplements based on category and search
  const filteredSupplements = useMemo(() => {
    return supplements.filter(supplement => {
      const matchesCategory = selectedCategory === 'All' || supplement.category === selectedCategory;
      const matchesSearch = supplement.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           supplement.category.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [selectedCategory, searchQuery]);

  return (
    <div className="space-y-8">
      {/* Back Button */}
      <Link
        to="/integrative-health"
        className="inline-flex items-center text-slate-600 hover:text-slate-900 transition-colors"
      >
        <ArrowLeft className="h-5 w-5 mr-2" />
        Back to Integrative Health
      </Link>

      {/* Header */}
      <div>
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-4">
          Supplements Library
        </h1>
        <p className="text-lg text-slate-700 leading-relaxed mb-6">
          Non-prescriptive information about vitamins, minerals, and supplements that are sometimes discussed in integrative health communities.
        </p>
      </div>

      {/* Disclaimer */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
        <p className="text-sm text-slate-700 leading-relaxed">
          This information is provided for educational purposes only and does not replace professional medical advice. Always consult your healthcare provider before making dietary, supplement, or lifestyle changes, especially if you are immunocompromised, have low blood counts, or are undergoing treatment.
        </p>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
        <input
          type="text"
          placeholder="Search supplements..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-12 pr-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        />
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap gap-3">
        <button
          onClick={() => setSelectedCategory('All')}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
            selectedCategory === 'All'
              ? 'bg-purple-600 text-white'
              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
          }`}
        >
          All ({supplements.length})
        </button>
        {supplementCategories.map(category => {
          const count = supplements.filter(s => s.category === category).length;
          return (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                selectedCategory === category
                  ? 'bg-purple-600 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {category} ({count})
            </button>
          );
        })}
      </div>

      {/* Results Count */}
      <div className="text-sm text-slate-600">
        Showing {filteredSupplements.length} of {supplements.length} supplements
        {searchQuery && ` for "${searchQuery}"`}
      </div>

      {/* Supplements Grid */}
      <div className="space-y-4">
        {filteredSupplements.length > 0 ? (
          filteredSupplements.map(supplement => (
            <SupplementCard key={supplement.id} supplement={supplement} />
          ))
        ) : (
          <div className="bg-white rounded-xl p-12 border border-slate-200 text-center">
            <p className="text-slate-600">
              No supplements found matching your criteria.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupplementsEducationalPage;